/**
 * The brand mark shown at the top of the sign-in / sign-up cards.
 *
 * A client who arrives via a DUAL-LICENSED agent's landing page is doing
 * business with two brokerages at once (a real-estate one and a mortgage one),
 * and the agent is required to identify both. Carrying only a single
 * `brandLogoUrl` through router state dropped the second one the moment the
 * client clicked "Create account", so the page they actually sign up on showed
 * half the relationship. `brands` carries every mark that applies.
 *
 * Router state is ephemeral (lost on reload or a second navigation hop), so a
 * direct visit to /login or /sign-up has no brands at all — that falls back to
 * the generic product mark rather than a mismatched lookup. There is
 * deliberately no by-tenant-id public lookup endpoint to recover it from; that
 * would be an enumeration surface for a cosmetic win.
 */

import type { ReactNode } from "react";

import { APP_NAME } from "../../brand";

export interface Brand {
  name: string;
  logoUrl: string | null;
}

/** Router state handed to /login and /sign-up by the public landing pages. */
export interface AuthNavState {
  tenantId?: string;
  /** Headline label — the agent's name on an agent page, else the brokerage's. */
  brandName?: string;
  /** Every brokerage whose mark belongs on the card; both when dual-licensed. */
  brands?: Brand[];
}

export function AuthBrand({
  brandName,
  brands = [],
  fallback,
}: {
  brandName?: string;
  brands?: Brand[];
  /** Icon shown when no logo is available — page-specific (key vs. door). */
  fallback: ReactNode;
}) {
  const withLogos = brands.filter((b) => b.logoUrl);

  return (
    <div className="signin__brand">
      {withLogos.length > 0
        ? withLogos.map((b) => (
            <img
              key={b.logoUrl}
              src={b.logoUrl ?? undefined}
              // Named rather than aria-hidden when there are two: which
              // brokerages these are IS the information, not decoration.
              alt={withLogos.length > 1 ? b.name : ""}
              aria-hidden={withLogos.length > 1 ? undefined : true}
              title={b.name}
              style={{
                height: 28,
                width: 28,
                objectFit: "contain",
                borderRadius: 4,
              }}
            />
          ))
        : fallback}
      <span>{brandName ?? brands[0]?.name ?? APP_NAME}</span>
    </div>
  );
}
